import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Home, 
  Search, 
  Calendar, 
  MessageSquare, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  User,
  LayoutDashboard,
  Building,
  Users,
  AlertTriangle,
  MapPin,
  HelpCircle,
  BookOpen,
  Info
} from 'lucide-react';
import { getInitials } from '../lib/utils';
import Footer from './Footer';

const Navigation = () => {
  const { user, logout, isAuthenticated, isHost, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const guestLinks = [
    { to: '/search', label: 'Find Stables', icon: Search },
    { to: '/trips', label: 'My Trips', icon: Calendar },
    { to: '/messages', label: 'Messages', icon: MessageSquare },
  ];

  const hostLinks = [
    { to: '/host/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/host/listings', label: 'My Listings', icon: Building },
    { to: '/host/requests', label: 'Requests', icon: Calendar },
    { to: '/messages', label: 'Messages', icon: MessageSquare },
  ];

  const adminLinks = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/listings', label: 'Listings', icon: Building },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/disputes', label: 'Disputes', icon: AlertTriangle },
  ];

  const getLinks = () => {
    if (isAdmin) return adminLinks;
    if (isHost) return hostLinks;
    return guestLinks;
  };

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <MapPin className="w-7 h-7 text-farmhouse-saddle" />
            <span className="font-serif text-xl font-semibold text-farmhouse-cream">
              Barns & Pastures
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {isAuthenticated ? (
              <>
                {getLinks().map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm transition-colors ${
                      location.pathname === link.to
                        ? 'text-farmhouse-saddle bg-farmhouse-saddle/10'
                        : 'text-farmhouse-muted hover:text-farmhouse-cream'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <link.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </Link>
                ))}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 ml-4"
                      data-testid="user-menu-trigger"
                    >
                      <div className="avatar bg-farmhouse-saddle text-white text-sm">
                        {getInitials(user?.name)}
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-farmhouse-paper border-farmhouse-subtle">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-farmhouse-cream">{user?.name}</p>
                      <p className="text-xs text-farmhouse-muted capitalize">{user?.role}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-farmhouse-subtle" />
                    <DropdownMenuItem 
                      className="cursor-pointer text-farmhouse-cream hover:bg-farmhouse-subtle"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer text-farmhouse-clay hover:bg-farmhouse-subtle"
                      onClick={handleLogout}
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link
                  to="/search"
                  className="text-farmhouse-muted hover:text-farmhouse-cream transition-colors text-sm font-medium"
                  data-testid="nav-browse"
                >
                  Browse Listings
                </Link>
                <Link
                  to="/how-it-works"
                  className="text-farmhouse-muted hover:text-farmhouse-cream transition-colors text-sm font-medium"
                >
                  How It Works
                </Link>
                <Link
                  to="/help"
                  className="text-farmhouse-muted hover:text-farmhouse-cream transition-colors text-sm font-medium"
                >
                  Help
                </Link>
                <Link to="/auth" data-testid="nav-signin">
                  <Button className="btn-primary text-sm">
                    Sign In
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-farmhouse-cream"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-farmhouse-paper border-t border-farmhouse-subtle animate-fade-in">
          <div className="px-4 py-4 space-y-2">
            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-3 px-3 py-3 border-b border-farmhouse-subtle mb-2">
                  <div className="avatar bg-farmhouse-saddle text-white text-sm">
                    {getInitials(user?.name)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-farmhouse-cream">{user?.name}</p>
                    <p className="text-xs text-farmhouse-muted capitalize">{user?.role}</p>
                  </div>
                </div>
                {getLinks().map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-3 px-3 py-3 rounded-sm ${
                      location.pathname === link.to
                        ? 'text-farmhouse-saddle bg-farmhouse-saddle/10'
                        : 'text-farmhouse-muted'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <link.icon className="w-5 h-5" />
                    <span>{link.label}</span>
                  </Link>
                ))}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-3 text-farmhouse-clay w-full"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/search"
                  className="block px-3 py-3 text-farmhouse-cream"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Browse Listings
                </Link>
                <Link
                  to="/auth"
                  className="block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="btn-primary w-full mt-2">Sign In</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export const Layout = ({ children, hideFooter = false }) => {
  return (
    <div className="min-h-screen bg-farmhouse-charcoal flex flex-col">
      <Navigation />
      <main className="pt-16 flex-1">{children}</main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default Layout;
